import { describeFlowLogicObject, stripFlowDescriptionMarkup } from '../services/flowDescriptionCore.js'

export interface TargetingCondition {
  $source?: string
  $type?: string
  operator?: string
  occurrence?: string
  query?: string
  value?: unknown
  domain?: string
  custom_referrer?: string
  and?: TargetingCondition[]
  or?: TargetingCondition[]
  not?: TargetingCondition
}

type PageVisibility = { conditions?: TargetingCondition | null }
export type PageRule = { domain: string | null; path: string | null }

const LISTED_PAGES = 3

export function summarizePageTargeting(visibility: PageVisibility | null | undefined): string {
  const conditions = visibility?.conditions
  if (!conditions) {
    return 'Everywhere'
  }
  if (conditions.or?.length === 0) {
    return 'No visitors (empty rule group)'
  }
  const page = pageConditionFromLogic(conditions)
  if (page) {
    return describePageCondition(page)
  }
  const pageOnly = summarizePageOnly(conditions)
  if (pageOnly) {
    return pageOnly
  }
  const description = describe(conditions)
  return description.charAt(0).toUpperCase() + description.slice(1)
}

// Page-only targeting (current domain + current page patterns) reads as "Only on …" / "Everywhere except …".
function summarizePageOnly(condition: TargetingCondition): string | null {
  const allowed = pageRules(condition)
  if (allowed) {
    return `Only on ${pagesPhrase(allowed)}`
  }
  const blocked = condition.not ? pageRules(condition.not) : null
  if (blocked) {
    return `Everywhere except ${pagesPhrase(blocked)}`
  }
  if (condition.and?.length === 2) {
    const [first, second] = condition.and
    for (const [allow, block] of [
      [first, second],
      [second, first]
    ]) {
      const allowRules = allow ? pageRules(allow) : null
      const blockRules = block?.not ? pageRules(block.not) : null
      if (allowRules && blockRules) {
        return `Only on ${pagesPhrase(allowRules)}, except ${pagesPhrase(blockRules)}`
      }
    }
  }
  return null
}

export function describe(condition: TargetingCondition): string {
  if (condition.not) {
    const blocked = pageRules(condition.not)
    return blocked ? `not on ${pagesPhrase(blocked)}` : `not (${describe(condition.not)})`
  }
  const children = condition.and ?? condition.or
  if (children) {
    if (!children.length) {
      return condition.and ? 'Everywhere' : 'No visitors'
    }
    const pages = pageRules(condition)
    if (pages) {
      return `on ${pagesPhrase(pages)}`
    }
    return children
      .map((child) => {
        const description = describe(child)
        return (child.and ?? child.or)?.length && children.length > 1 && !pageRules(child)
          ? `(${description})`
          : description
      })
      .join(condition.and ? ' and ' : ' or ')
  }
  const pages = pageRules(condition)
  if (pages) {
    return `on ${pagesPhrase(pages)}`
  }
  const page = pageConditionFromLogic(condition)
  if (page) {
    const label = describePageCondition(page)
    return label.charAt(0).toLowerCase() + label.slice(1)
  }
  return stripFlowDescriptionMarkup(describeFlowLogicObject(condition))
}

function isAnyDomain(value: string): boolean {
  return value === '*' || value.trim() === ''
}

/** Flattens a condition built only from current-domain and current-page patterns into page rules; null otherwise. */
export function pageRules(condition: TargetingCondition): PageRule[] | null {
  const only = condition.and?.length === 1 ? condition.and[0] : undefined
  if (only) {
    return pageRules(only)
  }
  // Pattern values are always strings; the snippet never matches any other value type.
  if (condition.$source === 'pageviews' && condition.operator === 'matches pattern') {
    const value = condition.value ?? ''
    if (typeof value !== 'string') {
      return null
    }
    if (condition.$type === 'path' && condition.occurrence === 'last') {
      return [{ domain: null, path: value }]
    }
    if (condition.$type === 'domain') {
      return [{ domain: isAnyDomain(value) ? null : value.trim(), path: null }]
    }
    return null
  }
  if (condition.or?.length) {
    const rules: PageRule[] = []
    for (const child of condition.or) {
      const childRules = pageRules(child)
      if (!childRules) {
        return null
      }
      rules.push(...childRules)
    }
    return rules
  }
  if (condition.and?.length === 2) {
    const [first, second] = condition.and
    for (const [domainLeaf, paths] of [
      [first, second],
      [second, first]
    ]) {
      if (domainLeaf?.$source === 'pageviews' && domainLeaf.$type === 'domain' && paths) {
        const domainRules = pageRules(domainLeaf)
        const pathRules = pageRules(paths)
        if (domainRules?.[0] && pathRules?.every((rule) => rule.domain === null && rule.path !== null)) {
          const { domain } = domainRules[0]
          return pathRules.map((rule) => ({ domain, path: rule.path }))
        }
      }
    }
  }
  return null
}

export function pageLabel({ domain, path }: PageRule): string {
  if (path === null || /^\/?\*$/.test(path)) {
    return domain ? `all of ${domain}` : 'every page'
  }
  const normalized = `/${path.replace(/^\//, '')}`
  return domain ? `${domain}${normalized}` : normalized
}

// Up to three pages are named ("/pricing, /blog/* or example.com/"); longer lists are counted per domain.
function pagesPhrase(rules: PageRule[]): string {
  if (rules.length <= LISTED_PAGES) {
    const labels = rules.map(pageLabel)
    return labels.length === 1 ? (labels[0] ?? '') : `${labels.slice(0, -1).join(', ')} or ${labels.at(-1)}`
  }
  const domains = [...new Set(rules.map((rule) => rule.domain).filter((domain) => domain !== null))]
  const across = domains.length
    ? ` across ${domains.length === 1 ? domains[0] : `${domains.slice(0, -1).join(', ')} and ${domains.at(-1)}`}`
    : ''
  return `${rules.length} pages${across}`
}
export interface PageConditionDescription {
  field?: string
  domain?: string
  operator?: string
  value?: string
  query?: string
  occurrence?: string
  negated?: boolean
  rule?: string
}

const operators: Record<string, string> = {
  glob: 'matches',
  'matches pattern': 'matches',
  regex: 'matches regex',
  'matches regex': 'matches regex',
  equals: 'is',
  'does not equal': 'is not',
  'does not contain': "doesn't contain",
  'include any': 'includes',
  'do not include any': "doesn't include"
}

const fields: Record<string, string> = {
  url: 'URL',
  path: 'Path',
  domain: 'Domain',
  query: 'Query parameter',
  referrer: 'Referrer',
  device: 'Device',
  landingpage: 'Landing page'
}

const patternOperator = (operator?: string): boolean => operator === 'glob' || operator === 'matches pattern'

export function isAnyPageCondition(condition: PageConditionDescription): boolean {
  if (condition.negated) {
    return false
  }
  const { field, operator, value = '', occurrence } = condition
  if (condition.domain !== undefined && condition.domain !== '*') {
    return false
  }
  if (field === 'domain') {
    return (
      (patternOperator(operator) || operator === 'equals' || operator === 'matches domain') &&
      (value === '*' || (patternOperator(operator) && !value.trim()))
    )
  }
  return (
    field === 'path' &&
    (!occurrence || occurrence === 'last' || occurrence === 'current') &&
    patternOperator(operator) &&
    /^\/?\*$/.test(value)
  )
}

export function describePageCondition(condition: PageConditionDescription): string {
  const { field, operator, value = '', occurrence } = condition
  let text: string
  if (isAnyPageCondition({ ...condition, negated: false })) {
    text = 'Any page'
  } else if (
    field === 'path' &&
    patternOperator(operator) &&
    (!occurrence || occurrence === 'last' || occurrence === 'current')
  ) {
    text = `Only on ${pageLabel({ domain: null, path: value })}`
  } else if (field === 'domain' && patternOperator(operator)) {
    text = `Only on ${pageLabel({ domain: value.trim(), path: null })}`
  } else if (field === 'domain' && (operator === 'equals' || operator === 'matches domain')) {
    text = `Only on ${pageLabel({ domain: value, path: null })}`
  } else if (field === 'referrer' && operator === 'is not set') {
    text = 'Direct traffic'
  } else if (field === 'referrer' && operator === 'is set') {
    text = 'Referred by any site'
  } else if (field === 'referrer' && operator === 'matches domain') {
    text = `Referred by ${value}`
  } else if (field && operator) {
    const label =
      field === 'query'
        ? `Query parameter${condition.query ? ` ${condition.query}` : ''}`
        : field === 'path' && occurrence === 'penultimate'
          ? 'Previous page'
          : field === 'path' && occurrence && occurrence !== 'last' && occurrence !== 'current'
            ? 'Visited page'
            : (fields[field] ?? field.replaceAll('_', ' '))
    const comparison = operators[operator] ?? operator
    text = `${label} ${comparison}${operator === 'is set' || operator === 'is not set' ? '' : ` ${value}`}`.trimEnd()
  } else {
    text = condition.rule || 'Any page'
  }
  if (condition.domain !== undefined && condition.domain !== '*') {
    text = `On ${condition.domain}: ${text}`
  }
  if (!condition.negated) {
    return text
  }
  return text === 'Any page'
    ? 'Everywhere except every page'
    : text.startsWith('Only on ')
      ? `Everywhere except ${text.slice(8)}`
      : `Not (${text})`
}

export function pageConditionFromLogic(condition: TargetingCondition): PageConditionDescription | null {
  if (condition.not) {
    const page = pageConditionFromLogic(condition.not)
    return page ? { ...page, negated: !page.negated } : null
  }
  const { $source: source, $type: type, operator, value, query, occurrence } = condition
  let text = ''
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    text = String(value)
  } else if (Array.isArray(value)) {
    if (
      !value.every((item: unknown) => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')
    ) {
      return null
    }
    text = value.join(', ')
  } else if (value !== undefined && value !== null) {
    return null
  }
  if (
    source === 'pageviews' &&
    (type === 'url' || type === 'domain' || type === 'landingpage' || (type === 'path' && occurrence === 'last'))
  ) {
    return { field: type, operator, value: text, occurrence }
  }
  if (source === 'query') {
    return { field: 'query', operator, value: text, query }
  }
  if (source === 'referrer') {
    return {
      field: 'referrer',
      operator:
        type === 'direct'
          ? 'is not set'
          : type === 'referral'
            ? 'is set'
            : type === 'domain'
              ? 'matches domain'
              : operator,
      value: type === 'domain' ? (condition.domain ?? '') : (condition.custom_referrer ?? '')
    }
  }
  return null
}
